import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import AcademicsFormField from '../../components/AcademicsFormField';
import PopupV2 from '../../../../Popup/PopupV2';
import { getPlayerStudentInfo, updatePlayerStudentInfo } from '../../../../../actions/profile-actions';
import styles from '../../collegeProspectTab.scss';

const TESTID_PREFIX = 'modal.profile.college-prospect.primary-contact-edit';

const RELATIONSHIP_OPTIONS = ['Player', 'Parent', 'Coach', 'Recruiting Service'];

const appendIfPresent = (formData, key, value) => {
    if (value !== null && value !== undefined && value !== '') {
        formData.append(key, value);
    }
};

const PrimaryContactEditModal = ({ onClose }) => {
    const dispatch = useDispatch();
    const studentInfo = useSelector((state) => state.profile?.data?.studentInfo) || {};
    const playerId = useSelector((state) => state.profile?.data?.id);
    const coach = studentInfo.coach || {};

    const [firstName, setFirstName] = useState(coach.firstName || '');
    const [lastName, setLastName] = useState(coach.lastName || '');
    const [relationship, setRelationship] = useState(coach.relationship || '');
    const [email, setEmail] = useState(coach.email || '');
    const [phone, setPhone] = useState(coach.phone || '');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('Context', 'onboardingCoach');
            appendIfPresent(formData, 'coach.firstName', firstName);
            appendIfPresent(formData, 'coach.lastName', lastName);
            appendIfPresent(formData, 'coach.relationship', relationship);
            appendIfPresent(formData, 'coach.email', email);
            appendIfPresent(formData, 'coach.phone', phone);

            const ok = await Promise.resolve(dispatch(updatePlayerStudentInfo(formData)));
            if (!ok) {
                throw new Error('save failed');
            }
            if (playerId) {
                await Promise.resolve(dispatch(getPlayerStudentInfo(playerId)));
            }
            onClose();
        } catch (err) {
            console.error('Primary contact save failed', err);
            setError('We couldn\'t save your primary contact. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PopupV2
            onCloseClick={onClose}
            config={{ size: 'medium', customHeader: () => null }}
        >
            <form
                onSubmit={handleSubmit}
                className={styles.academicsEditForm}
                data-testid={`${TESTID_PREFIX}.container`}
            >
                <div className={styles.academicsEditTitle} data-testid={`${TESTID_PREFIX}.title`}>
                    Edit Primary Contact
                </div>

                <AcademicsFormField label="First Name" testId="first-name" testIdPrefix={TESTID_PREFIX}>
                    <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First Name"
                        data-testid={`${TESTID_PREFIX}.first-name`}
                    />
                </AcademicsFormField>
                <AcademicsFormField label="Last Name" testId="last-name" testIdPrefix={TESTID_PREFIX}>
                    <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last Name"
                        data-testid={`${TESTID_PREFIX}.last-name`}
                    />
                </AcademicsFormField>
                <AcademicsFormField label="Relationship" testId="relationship" testIdPrefix={TESTID_PREFIX}>
                    <select
                        value={relationship}
                        onChange={(e) => setRelationship(e.target.value)}
                        data-testid={`${TESTID_PREFIX}.relationship`}
                    >
                        <option value="">Select relationship</option>
                        {RELATIONSHIP_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </AcademicsFormField>
                <AcademicsFormField label="Email" testId="email" testIdPrefix={TESTID_PREFIX}>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email"
                        data-testid={`${TESTID_PREFIX}.email`}
                    />
                </AcademicsFormField>
                <AcademicsFormField label="Phone Number" testId="phone" testIdPrefix={TESTID_PREFIX}>
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Phone Number"
                        data-testid={`${TESTID_PREFIX}.phone`}
                    />
                </AcademicsFormField>

                {error && (
                    <div className={styles.academicsEditError} data-testid={`${TESTID_PREFIX}.error`}>
                        {error}
                    </div>
                )}

                <div className={styles.academicsEditActions}>
                    <button
                        type="button"
                        className={styles.academicsEditCancelBtn}
                        onClick={onClose}
                        disabled={submitting}
                        data-testid={`${TESTID_PREFIX}.cancel-btn`}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={styles.academicsEditSaveBtn}
                        disabled={submitting}
                        data-testid={`${TESTID_PREFIX}.save-btn`}
                    >
                        {submitting ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </form>
        </PopupV2>
    );
};

PrimaryContactEditModal.propTypes = {
    onClose: PropTypes.func.isRequired,
};

export default PrimaryContactEditModal;
